import "./serviceProvider.css";

import { DEFAULT_COUNTRY, getCountryCodeOptions } from "../../../service/service";
import { useEffect, useMemo, useState } from "react";

import { API_URL } from "../../../../env";
import OtherServiceForm from "./otherServiceForm";
import SaloonAndSpa from "./saloonandSpa";
import axios from "axios";

const DEFAULT_SERVICE_HINT = "Choose a service type from dropdown, then the related form will populate below.";
const DEFAULT_COUNTRY_OPTIONS = [{ text: DEFAULT_COUNTRY.text, value: DEFAULT_COUNTRY.value }];
const BEAUTY_SERVICE_KEYWORDS = ["saloon", "salon", "spa", "beauty", "makeup", "hair", "bridal"];

const isBeautyCategory = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return BEAUTY_SERVICE_KEYWORDS.some((keyword) => normalized.includes(keyword));
};

const profilePayload = (payload) => payload?.responseData ?? payload ?? {};
const asArrayPayload = (payload) => {
  const resolved = payload?.responseData ?? payload ?? [];
  if (Array.isArray(resolved)) {
    return resolved;
  }

  if (Array.isArray(resolved?.items)) {
    return resolved.items;
  }

  return [];
};

const normalizeEndpointPath = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return value.startsWith("/") ? value : `/${value}`;
};

const formatServiceName = (value) => String(value || "").trim().toLowerCase();

const resolveServiceEndpoint = (selectedService, subMenuOptions) => {
  if (!selectedService) {
    return "";
  }

  const selectedName = formatServiceName(selectedService?.serviceName);
  const selectedId = Number(selectedService?.internalKey);

  const matchedSubMenu = subMenuOptions.find((row) => {
    const status = String(row?.status || "").toUpperCase();
    if (status && status !== "ACTIVE" && status !== "ONLINE") {
      return false;
    }

    const rowMenuName =
      formatServiceName(row?.serviceName) ||
      formatServiceName(row?.menuName) ||
      formatServiceName(row?.mainMenuName);

    const rowMenuId = Number(
      row?.menuInternalKey ?? row?.menuId ?? row?.serviceId ?? row?.serviceInternalKey
    );

    return rowMenuName === selectedName || (Number.isFinite(selectedId) && rowMenuId === selectedId);
  });

  if (!matchedSubMenu) {
    return "";
  }

  const candidateEndpoint =
    matchedSubMenu?.endpoint ||
    matchedSubMenu?.apiEndpoint ||
    matchedSubMenu?.apiUrl ||
    matchedSubMenu?.submitEndpoint ||
    matchedSubMenu?.url ||
    matchedSubMenu?.providerEndpoint ||
    "";

  return normalizeEndpointPath(candidateEndpoint);
};

const bindUserIdInEndpoint = (endpoint, userId) => {
  if (!endpoint) {
    return "";
  }

  const encodedUserId = encodeURIComponent(userId);

  return endpoint
    .replaceAll("{userId}", encodedUserId)
    .replaceAll(":userId", encodedUserId)
    .replaceAll("<userId>", encodedUserId);
};

const toRequestUrl = (apiBase, endpoint) => {
  if (!endpoint) {
    return "";
  }

  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }

  return `${apiBase}${endpoint}`;
};

const normalizeSubTypeItems = (rows) =>
  asArrayPayload(rows)
    .filter((row) => {
      const status = String(row?.status || "").toUpperCase();
      return !status || status === "ACTIVE" || status === "ONLINE";
    })
    .map((row) => {
      const name =
        row?.serviceSubMenuName ||
        row?.subMenuName ||
        row?.name ||
        row?.serviceName ||
        "";

      const numericRate = Number(
        row?.defaultRate ?? row?.rate ?? row?.price ?? row?.baseRate ?? row?.minimumRate ?? 0
      );

      return {
        id: String(row?.internalKey ?? row?.subMenuId ?? row?.serviceSubMenuId ?? name),
        name: String(name || "").trim(),
        defaultRate: Number.isFinite(numericRate) ? numericRate : 0
      };
    })
    .filter((row) => row.name);

const parseDobValue = (dob) => {
  if (!dob) {
    return "";
  }

  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Unable to process file: ${file.name}`));
    reader.readAsDataURL(file);
  });

const getInitialFormData = (profileView = {}) => ({
  genderToService: [],
  selectedSubTypes: [],
  yearOfExperience: "",
  availableFrom: "10:00",
  availableTill: "19:00",
  servesNight: false,
  rateBySubtype: {},
  providerPrimaryCountryCode: profileView?.primaryPhoneCountryCode || DEFAULT_COUNTRY.value,
  providerPrimaryPhoneNumber: "",
  providerSecondaryCountryCode: profileView?.primaryPhoneCountryCode || DEFAULT_COUNTRY.value,
  providerSecondaryPhoneNumber: "",
  useUserProfileNumberAsWorkNumber: false,
  shopConfig: {}
});

const ServiceProviderProfile = () => {
  const token = sessionStorage.getItem("token") || "";
  const userId = (sessionStorage.getItem("userId") || "").replaceAll('"', "").trim();

  const [profileLoading, setProfileLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedServiceType, setSelectedServiceType] = useState("");
  const [menuOptions, setMenuOptions] = useState([]);
  const [subMenuOptions, setSubMenuOptions] = useState([]);
  const [serviceSubTypes, setServiceSubTypes] = useState([]);
  const [serviceSubTypesLoading, setServiceSubTypesLoading] = useState(false);
  const [showProfileDetails, setShowProfileDetails] = useState(false);

  const [internalManaged, setInternalManaged] = useState({
    personalId: "",
    status: "ACTIVE",
    providerlat: "",
    providerLong: "",
    userLat: "",
    userLong: ""
  });

  const [profileView, setProfileView] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    dob: "",
    gender: "",
    primaryPhoneCountryCode: "",
    primaryPhoneNumber: ""
  });

  const [formData, setFormData] = useState(() => getInitialFormData());
  const [countryCodeOptions, setCountryCodeOptions] = useState(DEFAULT_COUNTRY_OPTIONS);

  const [pastExperienceCertificates, setPastExperienceCertificates] = useState([]);
  const [workProofFiles, setWorkProofFiles] = useState([]);

  useEffect(() => {
    const loadCountryCodes = async () => {
      const options = await getCountryCodeOptions();
      if (options.length > 0) {
        setCountryCodeOptions(options);
      }
    };

    loadCountryCodes();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      setProfileLoading(true);
      setError("");

      try {
        if (!userId) {
          throw new Error("User Id not found in session.");
        }

        const response = await axios.get(`${API_URL}/profile/getProfileBasedOnUserId/${encodeURIComponent(userId)}`, {
          headers: {
            env: "local",
            ...(token ? { token } : {})
          }
        });

        const profile = profilePayload(response?.data);

        setProfileView({
          firstName: profile?.firstName || "",
          middleName: profile?.middleName || "",
          lastName: profile?.lastName || "",
          dob: parseDobValue(profile?.dob),
          gender: profile?.gender || "",
          primaryPhoneCountryCode: profile?.primaryPhoneCountryCode || profile?.primaryCountryCode || "",
          primaryPhoneNumber: profile?.primaryPhoneNumber || profile?.mobile || ""
        });

        const personalId =
          sessionStorage.getItem("consumerorprovideId") ||
          profile?.personalId ||
          profile?.userData?.internalKey ||
          profile?.internalKey ||
          userId;

        setInternalManaged((prev) => ({
          ...prev,
          personalId: String(personalId || ""),
          userLat:
            profile?.userLat === null || profile?.userLat === undefined
              ? ""
              : String(profile?.userLat),
          userLong:
            profile?.userLong === null || profile?.userLong === undefined
              ? ""
              : String(profile?.userLong)
        }));
      } catch (profileError) {
        setError(profileError?.message || "Unable to load profile.");
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [token, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setInternalManaged((prev) => ({
          ...prev,
          providerlat: prev.providerlat || String(coords.latitude),
          providerLong: prev.providerLong || String(coords.longitude),
          userLat: prev.userLat || String(coords.latitude),
          userLong: prev.userLong || String(coords.longitude)
        }));
      },
      () => {}
    );
  }, []);

  useEffect(() => {
    const fetchMenuConfig = async () => {
      const requestHeaders = {
        env: "local",
        ...(token ? { token } : {})
      };

      const [menuResult, subMenuResult] = await Promise.allSettled([
        axios.get(`${API_URL}/profile/getMenu`, { headers: requestHeaders }),
        axios.get(`${API_URL}/profile/getSubMenu`, { headers: requestHeaders })
      ]);

      if (menuResult.status === "fulfilled") {
        const menuRows = asArrayPayload(menuResult.value?.data);
        setMenuOptions(
          menuRows.filter((row) => String(row?.status || "").toUpperCase() === "ACTIVE")
        );
      }

      if (subMenuResult.status === "fulfilled") {
        const subMenuRows = asArrayPayload(subMenuResult.value?.data);
        setSubMenuOptions(subMenuRows);
      }
    };

    fetchMenuConfig();
  }, [token]);

  useEffect(() => {
    if (!formData.useUserProfileNumberAsWorkNumber) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      providerPrimaryCountryCode: profileView.primaryPhoneCountryCode || prev.providerPrimaryCountryCode || DEFAULT_COUNTRY.value,
      providerPrimaryPhoneNumber: profileView.primaryPhoneNumber || ""
    }));
  }, [
    formData.useUserProfileNumberAsWorkNumber,
    profileView.primaryPhoneCountryCode,
    profileView.primaryPhoneNumber
  ]);

  const selectedService = useMemo(
    () => menuOptions.find((item) => item?.serviceName === selectedServiceType) || null,
    [menuOptions, selectedServiceType]
  );

  const isBeautyService = useMemo(
    () => isBeautyCategory(selectedServiceType),
    [selectedServiceType]
  );

  const serviceDescription = selectedService?.description?.trim() || DEFAULT_SERVICE_HINT;

  const selectedServiceEndpoint = useMemo(
    () => resolveServiceEndpoint(selectedService, subMenuOptions),
    [selectedService, subMenuOptions]
  );

  useEffect(() => {
    const loadServiceSubTypes = async () => {
      if (!selectedService?.internalKey) {
        setServiceSubTypes([]);
        return;
      }

      setServiceSubTypesLoading(true);

      try {
        const response = await axios.get(
          `${API_URL}/profile/getSubMenuBasedOnMenuId/${encodeURIComponent(selectedService.internalKey)}`,
          {
            headers: {
              env: "local",
              ...(token ? { token } : {})
            }
          }
        );

        setServiceSubTypes(normalizeSubTypeItems(response?.data));
      } catch (subTypeError) {
        console.error("Failed to fetch service subtypes", subTypeError);
        setServiceSubTypes([]);
      } finally {
        setServiceSubTypesLoading(false);
      }
    };

    loadServiceSubTypes();
  }, [selectedService?.internalKey, token]);

  const activeSubTypesForRateList = useMemo(() => {
    if (!isBeautyService) {
      return formData.selectedSubTypes;
    }

    const shopRows = formData.shopConfig?.shopOwnerProfile?.servicesOfferedInShop || [];
    const includeHome = formData.shopConfig?.createHomeServiceProfile === "yes";
    const homeRows = includeHome
      ? formData.shopConfig?.homeServiceProfile?.servicesOfferedAtHome || []
      : [];

    return Array.from(new Set([...shopRows, ...homeRows]));
  }, [formData.selectedSubTypes, formData.shopConfig, isBeautyService]);

  const serviceDefaultRateMap = useMemo(
    () =>
      serviceSubTypes.reduce((acc, row) => {
        acc[row.name] = row.defaultRate;
        return acc;
      }, {}),
    [serviceSubTypes]
  );

  const shouldShowHomeOnlyUploads = useMemo(() => {
    if (!isBeautyService) {
      return true;
    }

    return formData.shopConfig?.createHomeServiceProfile === "yes";
  }, [formData.shopConfig?.createHomeServiceProfile, isBeautyService]);

  const rateRows = useMemo(
    () =>
      activeSubTypesForRateList.map((subType) => {
        const match = serviceSubTypes.find((item) => item.name === subType);
        return {
          subType,
          defaultRate: match ? match.defaultRate : 0,
          yourRate: formData.rateBySubtype[subType] || ""
        };
      }),
    [activeSubTypesForRateList, formData.rateBySubtype, serviceSubTypes]
  );

  const toggleGender = (gender) => {
    setFormData((prev) => {
      const alreadySelected = prev.genderToService.includes(gender);
      return {
        ...prev,
        genderToService: alreadySelected
          ? prev.genderToService.filter((item) => item !== gender)
          : [...prev.genderToService, gender]
      };
    });
  };

  const toggleSubtype = (subType) => {
    setFormData((prev) => {
      const alreadySelected = prev.selectedSubTypes.includes(subType);
      const nextSubTypes = alreadySelected
        ? prev.selectedSubTypes.filter((item) => item !== subType)
        : [...prev.selectedSubTypes, subType];

      const nextRates = { ...prev.rateBySubtype };
      if (alreadySelected) {
        delete nextRates[subType];
      }

      return {
        ...prev,
        selectedSubTypes: nextSubTypes,
        rateBySubtype: nextRates
      };
    });
  };

  const pushFiles = (files, setter) => {
    if (!files || files.length === 0) {
      return;
    }

    const nextItems = Array.from(files).map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file)
    }));

    setter((prev) => [...prev, ...nextItems]);
  };

  const removeUploadedFile = (id, files, setter) => {
    const target = files.find((item) => item.id === id);
    if (target) {
      URL.revokeObjectURL(target.previewUrl);
    }
    setter((prev) => prev.filter((item) => item.id !== id));
  };

  const buildAttachmentPayload = async (rows) => {
    const mapped = await Promise.all(
      rows.map(async (row) => ({
        fileName: row.file.name,
        contentType: row.file.type || "application/octet-stream",
        size: row.file.size,
        content: await fileToDataUrl(row.file)
      }))
    );

    return mapped;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!userId) {
      setError("User Id is missing in session.");
      return;
    }

    if (!selectedServiceType) {
      setError("Please select service type.");
      return;
    }

    const shopOwnerProfile = formData.shopConfig?.shopOwnerProfile || {};
    const hasHomeProfile = formData.shopConfig?.createHomeServiceProfile === "yes";
    const homeServiceProfile = formData.shopConfig?.homeServiceProfile || {};

    const selectedGenderForSubmit = isBeautyService
      ? shopOwnerProfile.genderToService || []
      : formData.genderToService;

    const selectedSubTypesForSubmit = isBeautyService
      ? activeSubTypesForRateList
      : formData.selectedSubTypes;

    const yearsOfExperienceForSubmit = isBeautyService
      ? formData.shopConfig?.shopExperienceYears || ""
      : formData.yearOfExperience;

    if (selectedGenderForSubmit.length === 0) {
      setError("Please select at least one gender option.");
      return;
    }

    if (selectedSubTypesForSubmit.length === 0) {
      setError("Please select at least one service subtype.");
      return;
    }

    if (!yearsOfExperienceForSubmit) {
      setError("Please enter experience.");
      return;
    }

    if (isBeautyService && hasHomeProfile && (homeServiceProfile.genderToService || []).length === 0) {
      setError("Please select at least one home profile gender option.");
      return;
    }

    if (isBeautyService && formData.shopConfig?.listShopEmployees === "yes") {
      const hasInvalidShopEmployee = (formData.shopConfig?.shopEmployees || []).some(
        (row) => !String(row?.employeeName || "").trim() || !String(row?.experienceYears || "").trim()
      );

      if (hasInvalidShopEmployee) {
        setError("Please fill employee name and experience for all shop employees.");
        return;
      }
    }

    if (
      isBeautyService &&
      hasHomeProfile &&
      ["employees", "both"].includes(formData.shopConfig?.homeServiceHandledBy || "owner")
    ) {
      const hasInvalidHomeEmployee = (formData.shopConfig?.homeServiceEmployees || []).some(
        (row) => !String(row?.employeeName || "").trim() || !String(row?.aadhar || "").trim()
      );

      if (hasInvalidHomeEmployee) {
        setError("For home service employees, name and aadhar are mandatory.");
        return;
      }
    }

    setSubmitLoading(true);

    try {
      const shopRateList = isBeautyService
        ? (formData.shopConfig?.shopOwnerProfile?.servicesOfferedInShop || []).map((serviceName) => ({
            profileType: "shop",
            subType: serviceName,
            defaultRate: serviceDefaultRateMap[serviceName] || 0,
            yourRate:
              formData.shopConfig?.shopRateBySubtype?.[serviceName] === ""
                ? null
                : Number(formData.shopConfig?.shopRateBySubtype?.[serviceName] || 0)
          }))
        : [];

      const homeRateList = isBeautyService
        ? (formData.shopConfig?.homeServiceProfile?.servicesOfferedAtHome || []).map((serviceName) => ({
            profileType: "home",
            subType: serviceName,
            defaultRate: serviceDefaultRateMap[serviceName] || 0,
            yourRate:
              formData.shopConfig?.homeRateBySubtype?.[serviceName] === ""
                ? null
                : Number(formData.shopConfig?.homeRateBySubtype?.[serviceName] || 0)
          }))
        : [];

      const rateList = isBeautyService
        ? [...shopRateList, ...homeRateList]
        : rateRows.map((row) => ({
            subType: row.subType,
            defaultRate: row.defaultRate,
            yourRate: row.yourRate === "" ? null : Number(row.yourRate)
          }));

      const certificates = shouldShowHomeOnlyUploads
        ? await buildAttachmentPayload(pastExperienceCertificates)
        : [];
      const workProofs = shouldShowHomeOnlyUploads
        ? await buildAttachmentPayload(workProofFiles)
        : [];
      const shopImageFiles = await buildAttachmentPayload(formData.shopConfig?.shopImages || []);
      const shopVideoFiles = await buildAttachmentPayload(formData.shopConfig?.shopVideos || []);
      const shopExperienceProofFiles = await buildAttachmentPayload(formData.shopConfig?.shopExperienceProofs || []);
      const homeImageFiles = await buildAttachmentPayload(formData.shopConfig?.homeImages || []);
      const homeVideoFiles = await buildAttachmentPayload(formData.shopConfig?.homeVideos || []);
      const homeExperienceProofFiles = await buildAttachmentPayload(formData.shopConfig?.homeExperienceProofs || []);

      const providerPrimaryCountryCode = isBeautyService
        ? shopOwnerProfile.primaryCountryCode || formData.providerPrimaryCountryCode || null
        : formData.providerPrimaryCountryCode || null;
      const providerPrimaryPhoneNumber = isBeautyService
        ? shopOwnerProfile.primaryPhoneNumber || formData.providerPrimaryPhoneNumber || null
        : formData.providerPrimaryPhoneNumber || null;
      const providerSecondaryCountryCode = isBeautyService
        ? shopOwnerProfile.secondaryCountryCode || formData.providerSecondaryCountryCode || null
        : formData.providerSecondaryCountryCode || null;
      const providerSecondaryPhoneNumber = isBeautyService
        ? shopOwnerProfile.secondaryPhoneNumber || formData.providerSecondaryPhoneNumber || null
        : formData.providerSecondaryPhoneNumber || null;

      const availability = isBeautyService && hasHomeProfile
        ? {
            from: homeServiceProfile.availableFrom || "10:00",
            till: homeServiceProfile.availableTill || "19:00",
            servesNight: Boolean(homeServiceProfile.servesNight)
          }
        : {
            from: formData.availableFrom,
            till: formData.availableTill,
            servesNight: Boolean(formData.servesNight)
          };

      const payload = {
        serviceType: selectedServiceType,
        beautyServiceName: selectedServiceType,
        saloonSpaServiceName: selectedServiceType,
        serviceSubMenuName: selectedSubTypesForSubmit,
        serviceSubMenuNames: selectedSubTypesForSubmit,
        providerprimaryCountryCode: providerPrimaryCountryCode,
        providerprimaryPhoneNumber: providerPrimaryPhoneNumber,
        providersecondaryCountryCode: providerSecondaryCountryCode,
        providerSecondaryPhoneNumber: providerSecondaryPhoneNumber,
        providerPrimaryCountryCode,
        providerPrimaryPhoneNumber,
        providerSecondaryCountryCode,
        providersecondaryPhoneNumber: providerSecondaryPhoneNumber,
        personalId: internalManaged.personalId,
        status: internalManaged.status,
        userLat: internalManaged.userLat === "" ? null : Number(internalManaged.userLat),
        userLong: internalManaged.userLong === "" ? null : Number(internalManaged.userLong),
        providerlat: internalManaged.providerlat === "" ? null : Number(internalManaged.providerlat),
        providerLong: internalManaged.providerLong === "" ? null : Number(internalManaged.providerLong),
        genderToService: selectedGenderForSubmit,
        subTypes: selectedSubTypesForSubmit,
        yearOfExperience: yearsOfExperienceForSubmit,
        availability,
        shopConfiguration: {
          ...(formData.shopConfig || {}),
          createHomeServiceProfile: formData.shopConfig?.createHomeServiceProfile || formData.shopConfig?.providesHomeService || "no",
          providesHomeService: formData.shopConfig?.createHomeServiceProfile || formData.shopConfig?.providesHomeService || "no",
          shopImages: shopImageFiles,
          shopVideos: shopVideoFiles,
          shopExperienceProofs: shopExperienceProofFiles,
          homeImages: homeImageFiles,
          homeVideos: homeVideoFiles,
          homeExperienceProofs: homeExperienceProofFiles
        },
        shopRateList,
        homeRateList,
        rateList,
        pastExperienceCertificates: certificates,
        workProofs
      };

      const requestHeaders = {
        env: "local",
        ...(token ? { token } : {})
      };

      const endpointWithUser = bindUserIdInEndpoint(selectedServiceEndpoint, userId);
      const selectedServiceRequestUrl = toRequestUrl(API_URL, endpointWithUser);

      try {
        if (selectedServiceRequestUrl) {
          await axios.post(selectedServiceRequestUrl, payload, {
            headers: requestHeaders
          });
        } else {
          await axios.post(`${API_URL}/profile/addProvider/${encodeURIComponent(userId)}`, payload, {
            headers: requestHeaders
          });
        }
      } catch (postError) {
        if (!selectedServiceRequestUrl) {
          await axios.post(`${API_URL}/profile/addProvider/userId`, payload, {
            headers: requestHeaders
          });
        }
        if (!postError?.response) {
          throw postError;
        }
      }

      sessionStorage.setItem("vbIsProvider", "true");
      sessionStorage.setItem("vbActiveRole", "provider");
      const currentUser = JSON.parse(sessionStorage.getItem("vbUser") || "{}");
      if (!currentUser.providerId) {
        sessionStorage.setItem("vbUser", JSON.stringify({ ...currentUser, providerId: "enabled" }));
      }

      setSuccessMessage(`${selectedServiceType} profile saved successfully.`);
      setFormData(getInitialFormData(profileView));

      pastExperienceCertificates.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      workProofFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      setPastExperienceCertificates([]);
      setWorkProofFiles([]);
    } catch (submitError) {
      const apiMessage = submitError?.response?.data?.message;
      setError(apiMessage || submitError?.message || "Unable to save provider service.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="sp-page">
      <section className="sp-hero">
        <div>
          <span className="sp-eyebrow">Provider Services</span>
          <h1>{selectedServiceType || "Choose Service Type"}</h1>
          <p>{serviceDescription}</p>
        </div>

        <div className="sp-stats">
          <strong>Service Type</strong>
          <span>{selectedServiceType || "Not selected"}</span>
        </div>
      </section>

      <section className="sp-card">
        <form onSubmit={handleSubmit}>
          <h2 className="sp-section-title">Choose Service</h2>
          <div className="sp-grid">
            <label className="sp-field sp-full">
              <span>Service Type *</span>
              <select
                value={selectedServiceType}
                onChange={(event) => {
                  setSelectedServiceType(event.target.value);
                  setError("");
                  setSuccessMessage("");
                  setFormData(getInitialFormData(profileView));
                  pastExperienceCertificates.forEach((item) => URL.revokeObjectURL(item.previewUrl));
                  workProofFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
                  setPastExperienceCertificates([]);
                  setWorkProofFiles([]);
                }}
              >
                <option value="">Select Service Type</option>
                {menuOptions.map((option) => (
                  <option key={option.internalKey || option.serviceName} value={option.serviceName}>
                    {option.serviceName}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {selectedServiceType && (
            <>
          <section className="sp-profile-block">
            <button
              type="button"
              className="sp-accordion-toggle"
              onClick={() => setShowProfileDetails((value) => !value)}
              aria-expanded={showProfileDetails}
            >
              <span>Profile Details</span>
              <i>{showProfileDetails ? "Hide" : "Show"}</i>
            </button>

            {showProfileDetails && (
              <div className="sp-grid sp-grid-profile">
                <label className="sp-field">
                  <span>First Name</span>
                  <input type="text" value={profileView.firstName} disabled />
                </label>
                <label className="sp-field">
                  <span>Middle Name</span>
                  <input type="text" value={profileView.middleName} disabled />
                </label>
                <label className="sp-field">
                  <span>Last Name</span>
                  <input type="text" value={profileView.lastName} disabled />
                </label>
                <label className="sp-field">
                  <span>Date Of Birth</span>
                  <input type="date" value={profileView.dob} disabled />
                </label>
                <label className="sp-field">
                  <span>Gender</span>
                  <input type="text" value={profileView.gender} disabled />
                </label>
                <label className="sp-field">
                  <span>Primary Phone Code</span>
                  <input type="text" value={profileView.primaryPhoneCountryCode} disabled />
                </label>
                <label className="sp-field">
                  <span>Primary Phone Number</span>
                  <input type="text" value={profileView.primaryPhoneNumber} disabled />
                </label>
              </div>
            )}
          </section>

          {isBeautyService ? (
            <SaloonAndSpa
              value={formData.shopConfig}
              serviceOptions={serviceSubTypes}
              countryCodeOptions={countryCodeOptions}
              defaultCountryCode={profileView.primaryPhoneCountryCode || DEFAULT_COUNTRY.value}
              onChange={(nextValue) =>
                setFormData((prev) => ({
                  ...prev,
                  shopConfig: nextValue
                }))
              }
            />
          ) : (
            <OtherServiceForm
              formData={formData}
              setFormData={setFormData}
              countryCodeOptions={countryCodeOptions}
              profileView={profileView}
              serviceSubTypes={serviceSubTypes}
              serviceSubTypesLoading={serviceSubTypesLoading}
              toggleGender={toggleGender}
              toggleSubtype={toggleSubtype}
            />
          )}

          {shouldShowHomeOnlyUploads && (
            <>
              <h2 className="sp-section-title">Past Experience Certificates</h2>
              <label className="sp-upload-label">
                <input
                  type="file"
                  multiple
                  onChange={(event) => {
                    pushFiles(event.target.files, setPastExperienceCertificates);
                    event.target.value = "";
                  }}
                />
                <span>Upload certificates / course documents (multiple)</span>
              </label>
              <div className="sp-upload-list">
                {pastExperienceCertificates.map((item) => (
                  <article key={item.id}>
                    {item.file.type.startsWith("image/") ? (
                      <img src={item.previewUrl} alt={item.file.name} />
                    ) : (
                      <div className="sp-file-placeholder">FILE</div>
                    )}
                    <div>
                      <strong>{item.file.name}</strong>
                      <small>{Math.round(item.file.size / 1024)} KB</small>
                    </div>
                    <button type="button" onClick={() => removeUploadedFile(item.id, pastExperienceCertificates, setPastExperienceCertificates)}>
                      Delete
                    </button>
                  </article>
                ))}
              </div>

              <h2 className="sp-section-title">Proof Of Work Done</h2>
              <label className="sp-upload-label">
                <input
                  type="file"
                  multiple
                  onChange={(event) => {
                    pushFiles(event.target.files, setWorkProofFiles);
                    event.target.value = "";
                  }}
                />
                <span>Upload your work samples (multiple)</span>
              </label>
              <div className="sp-upload-list">
                {workProofFiles.map((item) => (
                  <article key={item.id}>
                    {item.file.type.startsWith("image/") ? (
                      <img src={item.previewUrl} alt={item.file.name} />
                    ) : (
                      <div className="sp-file-placeholder">FILE</div>
                    )}
                    <div>
                      <strong>{item.file.name}</strong>
                      <small>{Math.round(item.file.size / 1024)} KB</small>
                    </div>
                    <button type="button" onClick={() => removeUploadedFile(item.id, workProofFiles, setWorkProofFiles)}>
                      Delete
                    </button>
                  </article>
                ))}
              </div>
            </>
          )}

          {!isBeautyService && (
            <>
              <h2 className="sp-section-title">Rate List</h2>
              <div className="sp-rate-list">
                {rateRows.length === 0 && <p>Select sub types to create rate list.</p>}
                {rateRows.map((row) => (
                  <div key={row.subType} className="sp-rate-row">
                    <strong>{row.subType}</strong>
                    <label className="sp-rate-input-group">
                      <span>Default Price</span>
                      <input
                        type="text"
                        value={`INR ${row.defaultRate}`}
                        disabled
                        readOnly
                      />
                    </label>
                    <label className="sp-rate-input-group">
                      <span>Your Rate</span>
                      <input
                        type="number"
                        min="0"
                        value={row.yourRate}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            rateBySubtype: {
                              ...prev.rateBySubtype,
                              [row.subType]: event.target.value
                            }
                          }))
                        }
                      />
                    </label>
                  </div>
                ))}
              </div>
            </>
          )}
            </>
          )}

          {profileLoading && <p className="sp-info">Loading profile defaults...</p>}
          {error && <p className="sp-error">{error}</p>}
          {successMessage && <p className="sp-success">{successMessage}</p>}

          <div className="sp-actions">
            <button
              type="button"
              className="sp-reset"
              onClick={() => {
                setFormData(getInitialFormData(profileView));
                pastExperienceCertificates.forEach((item) => URL.revokeObjectURL(item.previewUrl));
                workProofFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
                setPastExperienceCertificates([]);
                setWorkProofFiles([]);
              }}
              disabled={submitLoading}
            >
              Reset Form
            </button>
            <button type="submit" className="sp-submit" disabled={submitLoading || profileLoading}>
              {submitLoading ? "Saving..." : "Save Service"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default ServiceProviderProfile;
